import React from "react";

// --- VEGAS CALCULATION ENGINE ---
const calculateVegasScores = (teamA, teamB, scores, holes, playerHandicaps = {}, presses = null) => {
  let totalA = 0, totalB = 0;
  let pressedA = 0, pressedB = 0;
  let standardA = 0, standardB = 0;

  const getStrokes = (hcp, holeDifficulty) => {
    if (!holeDifficulty) return 0;
    const baseStrokes = Math.floor(hcp / 18);
    const extraStroke = holeDifficulty <= hcp % 18 ? 1 : 0;
    return baseStrokes + extraStroke;
  };

  holes.forEach((h, i) => {
    const holeNum = i + 1;
    const nine = holeNum <= 9 ? "front" : "back";

    const teamAPress = presses?.[nine]?.teamA;
    const teamBPress = presses?.[nine]?.teamB;
    const isAPressed = teamAPress !== null && teamAPress !== undefined && holeNum >= teamAPress;
    const isBPressed = teamBPress !== null && teamBPress !== undefined && holeNum >= teamBPress;
    const isPressed = isAPressed || isBPressed;

    const aScores = teamA.map(p => {
      const gross = parseInt(scores[p]?.[holeNum], 10);
      if (isNaN(gross)) return NaN;
      const hcp = typeof playerHandicaps[p] === "object" ? playerHandicaps[p].current : playerHandicaps[p] || 0;
      return gross - getStrokes(hcp, h.difficulty);
    }).filter(s => !isNaN(s));

    const bScores = teamB.map(p => {
      const gross = parseInt(scores[p]?.[holeNum], 10);
      if (isNaN(gross)) return NaN;
      const hcp = typeof playerHandicaps[p] === "object" ? playerHandicaps[p].current : playerHandicaps[p] || 0;
      return gross - getStrokes(hcp, h.difficulty);
    }).filter(s => !isNaN(s));

    if (aScores.length === teamA.length && bScores.length === teamB.length) {
      const t1Sorted = [...aScores].sort((a, b) => a - b);
      const t2Sorted = [...bScores].sort((a, b) => a - b);

      let t1Points = Number(`${t1Sorted[0]}${t1Sorted[1]}`);
      let t2Points = Number(`${t2Sorted[0]}${t2Sorted[1]}`);

      const t1Birdie = t1Sorted[0] < h.par;
      const t2Birdie = t2Sorted[0] < h.par;

      if (t1Birdie && !t2Birdie) {
        t2Points = Number(`${t2Sorted[1]}${t2Sorted[0]}`);
      } else if (t2Birdie && !t1Birdie) {
        t1Points = Number(`${t1Sorted[1]}${t1Sorted[0]}`);
      }

      const multiplier = isPressed ? 2 : 1;
      totalA += t1Points * multiplier;
      totalB += t2Points * multiplier;

      if (isPressed) {
        pressedA += t1Points;
        pressedB += t2Points;
      } else {
        standardA += t1Points;
        standardB += t2Points;
      }
    }
  });

  return { totalA, totalB, pressedA, pressedB, standardA, standardB };
};

export default function Leaderboard({
  players,
  scores,
  selectedCourse,
  roundDate,
  isHistorical,
  setView,
  onBack,
  setStatsTargetPlayer,
  selectedGames = [],
  matchConfig,
  vegasConfig = null,
  vegasPointValue = 0,
  vegasPresses = null,
  onPress = null,
  playerHandicaps = {},
  isFinalized,
  onSelectPlayer,
  keptHoles = {},
  isMajor = false,
  majorName = "",
  playerPhones = {},
  onTrackStats,
  playerDirectory = [],
  onFinish,
  roundId,
  roundHistory = [],
  onSelectRound = null,
  roundHandicapOverrides = {},
  isPublicView = false,
  shareId = null,
}) {
  const SHOW_VEGAS_PRESS = false;

  // --- HELPER: Resolve display name from UUID or object ---
  const getName = (p) => {
    if (typeof p === "object" && p?.full_name) return p.full_name;
    const profile = playerDirectory?.find((dir) => dir.id === p);
    return profile?.full_name || p;
  };
  // --- VENMO LOGIC ---
  const handleVenmoPayment = (playerName) => {
    const note = encodeURIComponent(
      `Golf Bet: ${selectedCourse?.name || "Round"}`
    );
    const venmoUrl = `venmo://paycharge?txn=pay&note=${note}`;
    window.location.href = venmoUrl;
  };

  // --- SHARE SUMMARY LOGIC (NATIVE SMS) ---
  // --- SHARE SUMMARY LOGIC (NATIVE SMS) ---
  const handleShareSummary = () => {
    const phoneNumbers = players
      .map((p) => {
        const name = getName(p);
        const pId = typeof p === "object" ? p.id : p;
        const profile = playerDirectory?.find((dir) => dir.id === pId);
        return profile?.phone_number || null;
      })
      .filter((num) => !!num)
      .map((num) => num.replace(/\D/g, ""))
      .map((num) => (num.length === 10 ? `+1${num}` : `+${num}`))
      .join(",");

    console.log("players for SMS:", players.map(p => typeof p === "object" ? p.id : p));
    console.log("playerDirectory phone lookup:", players.map(p => {
      const pId = typeof p === "object" ? p.id : p;
      const profile = playerDirectory?.find((dir) => dir.id === pId);
      return { id: pId, name: profile?.full_name, phone: profile?.phone_number };
    }));
    console.log("final phoneNumbers string:", phoneNumbers);

    const date = new Date(roundDate).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });

    let message = `⛳ ${selectedCourse?.name || "Golf"} - ${date}\n\n`;

    // 1. Net Leaderboard
    message += `NET RESULTS\n`;
    sortedPlayers.forEach((p, i) => {
      const pId = typeof p === "object" ? p.id : p;
      const name = typeof p === "object" ? p.full_name : getName(p);
      const lastName = getLastName(name);
      const pScores = scores[pId] || {};

      const gross = Object.values(pScores).reduce((sum, s) => {
        const n = Number(s);
        return sum + (isNaN(n) ? 0 : n);
      }, 0);
      const grossTotal = pScores.total !== undefined ? Number(pScores.total) : gross;

      const rawHcp = playerHandicaps[pId] || 0;
      const hcp = typeof rawHcp === "object" ? rawHcp.current : rawHcp;
      const netTotal = grossTotal - hcp;

      const pos = i + 1;
      message += `${pos}. ${lastName}: ${grossTotal} gross / ${netTotal} net (HCP ${hcp})\n`;
    });

    // 2. Quota Points
    if (selectedGames.includes("Quota Points")) {
      message += `\nQUOTA\n`;
      const sortedQuota = [...players].sort((a, b) => {
        const idA = typeof a === "object" ? a.id : a;
        const idB = typeof b === "object" ? b.id : b;
        return (
          parseInt(getPlayerStats(idB).quotaRel) -
          parseInt(getPlayerStats(idA).quotaRel)
        );
      });
      sortedQuota.forEach((p) => {
        const pId = typeof p === "object" ? p.id : p;
        const name = getName(p);
        message += `${getLastName(name)}: ${getPlayerStats(pId).quotaRel}\n`;
      });
    }

    // 3. Match Play
    if (selectedGames.includes("Match Play") && matchConfig) {
      message += `\nMATCH PLAY\n`;
      message += `Total: ${getMatchStatus(1, 18)}\n`;
      message += `F9: ${getMatchStatus(1, 9)} | B9: ${getMatchStatus(
        10,
        18
      )}\n`;
    }

    // 4. Vegas
    if (selectedGames.includes("Vegas") && vegasConfig) {
      const { totalA, totalB } = calculateVegasScores(
        vegasConfig.teamA,
        vegasConfig.teamB,
        scores,
        selectedCourse?.holes || [],
        playerHandicaps
      );
      message += `\nVEGAS\n`;
      message += `Team A: ${totalA} | Team B: ${totalB}\n`;
    }

    // 5. 11's Game
    if (selectedGames.includes("11's")) {
      const winner = sortedElevens[0];
      const name = getName(winner);
      const winnerId = typeof winner === "object" ? winner.id : winner;
      const winStats = getElevensStats(winnerId);
      message += `\n11'S: ${getLastName(name)} wins (${winStats.netRel})\n`;
    }

    // 6. Skins
    if (selectedGames.includes("Skins")) {
      message += `\nSKINS\n`;
      let skinsFound = false;
      sortedSkinsPlayers.forEach((p) => {
        const pId = typeof p === "object" ? p.id : p;
        const name = getName(p);
        if (skinWinners[pId] > 0) {
          message += `${getLastName(name)}: ${skinWinners[pId]}\n`;
          skinsFound = true;
        }
      });
      if (!skinsFound) message += "No skins\n";
    }

    // Deep link back to round summary
    // roundId comes from props
    // Deep link back to round summary
    if (roundId) {
      message += `\n\n📲 Full leaderboard: https://confident-ardinghelli-1.pages.dev/?view=leaderboard&round=${roundId}`;
    }
    const encodedMsg = encodeURIComponent(message);
    window.location.href = `sms:${phoneNumbers}?&body=${encodedMsg}`;

    // Find players who made a 2 and have a phone number
    const twosPhoneNumbers = players
      .filter((p) => {
        const pId = typeof p === "object" ? p.id : p;
        const playerScores = scores[pId] || {};
        return Object.values(playerScores).some((s) => Number(s) === 2);
      })
      .map((p) => {
        const name = typeof p === "object" ? p.full_name : p;
        const profile = playerDirectory?.find((dir) => dir.full_name === name);
        return profile?.phone_number || null;
      })
      .filter((num) => !!num)
      .map((num) => num.replace(/\D/g, ""))
      .map((num) => (num.length === 10 ? `+1${num}` : `+${num}`))
      .join(",");

    if (twosPhoneNumbers) {
      const diceMessage = `🎲 You made a 2 today — time to roll!\nhttps://confident-ardinghelli-1.pages.dev/?view=diceRoller`;
      const encodedDiceMsg = encodeURIComponent(diceMessage);
      setTimeout(() => {
        window.location.href = `sms:${twosPhoneNumbers}?&body=${encodedDiceMsg}`;
      }, 1000);
    }
  };

  const getLastName = (p) => {
    const name = typeof p === "object" ? p.full_name : getName(p);
    return name ? name.split(" ").pop() : "";
  };

  // --- HELPER: Unified Stroke Calculation ---
  const getStrokesForHole = (hcp, holeDifficulty) => {
    if (!holeDifficulty) return 0;
    const baseStrokes = Math.floor(hcp / 18);
    const extraStroke = holeDifficulty <= hcp % 18 ? 1 : 0;
    return baseStrokes + extraStroke;
  };

  // --- HELPER: Quota Points ---
  const getQuotaPoints = (score, par) => {
    const diff = score - par;
    if (diff <= -2) return 8;
    if (diff === -1) return 4;
    if (diff === 0) return 2;
    if (diff === 1) return 1;
    return 0;
  };

  // --- STATS CALCULATION ---
  const getPlayerStats = (p) => {
    const pId = typeof p === "object" ? p.id : p;
    const pScores = scores[pId] || {};

    // Handle quick entry format { total: N }
    if (pScores.total !== undefined) {
      const gross = Number(pScores.total);
      const rawHcp = playerHandicaps[pId] || 0;
      const hcp = typeof rawHcp === "object" ? rawHcp.current : rawHcp;
      const coursePar = selectedCourse?.holes?.reduce((sum, h) => sum + h.par, 0) || 72;
      const grossRel = gross - coursePar;
      const netScore = gross - hcp;
      const netRel = netScore - coursePar;
      return {
        quotaRel: "—",
        grossRel: grossRel === 0 ? "E" : grossRel > 0 ? `+${grossRel}` : grossRel,
        netRel: netRel === 0 ? "E" : netRel > 0 ? `+${netRel}` : netRel,
        rawNetRel: netRel,
        hcp: hcp,
      };
    }

    let currentQuotaPts = 0;
    let totalGrossRel = 0;
    let totalNetRel = 0;

    const rawHcp = playerHandicaps[pId] || 0;
    const hcp = typeof rawHcp === "object" ? rawHcp.current : rawHcp;
    const quotaTarget = 36 - hcp;

    selectedCourse?.holes.forEach((h, i) => {
      const holeNum = i + 1;
      const rawScore = pScores[holeNum];

      if (rawScore) {
        const val = parseInt(rawScore, 10);
        totalGrossRel += val - h.par;
        currentQuotaPts += getQuotaPoints(val, h.par);

        const strokes = getStrokesForHole(hcp, h.difficulty);
        const netScore = val - strokes;
        totalNetRel += netScore - h.par;
      }
    });

    return {
      quotaRel:
        currentQuotaPts - quotaTarget > 0
          ? `+${currentQuotaPts - quotaTarget}`
          : currentQuotaPts - quotaTarget,
      grossRel:
        totalGrossRel === 0
          ? "E"
          : totalGrossRel > 0
          ? `+${totalGrossRel}`
          : totalGrossRel,
      netRel:
        totalNetRel === 0
          ? "E"
          : totalNetRel > 0
          ? `+${totalNetRel}`
          : totalNetRel,
      rawNetRel: totalNetRel,
      hcp: hcp,
    };
  };

  // --- MATCH PLAY STATUS ---
  const getMatchStatus = (startHole, endHole) => {
    let aWins = 0,
      bWins = 0,
      playedCount = 0;
    const totalHolesInRange = endHole - startHole + 1;
    const { teamA, teamB } = matchConfig || {};
    if (!teamA || !teamB) return "Setup Teams";

    for (let i = startHole; i <= endHole; i++) {
      const hole = selectedCourse?.holes[i - 1];
      const aScores = teamA.map((p) => scores[p]?.[i]);
      const bScores = teamB.map((p) => scores[p]?.[i]);

      if (aScores.every((s) => s > 0) && bScores.every((s) => s > 0)) {
        playedCount++;
        const aBest = Math.min(
          ...teamA.map(
            (p) =>
              parseInt(scores[p][i], 10) -
              getStrokesForHole(playerHandicaps[p], hole.difficulty)
          )
        );
        const bBest = Math.min(
          ...teamB.map(
            (p) =>
              parseInt(scores[p][i], 10) -
              getStrokesForHole(playerHandicaps[p], hole.difficulty)
          )
        );
        if (aBest < bBest) aWins++;
        else if (bBest < aBest) bWins++;
      }
    }
    if (playedCount === 0) return "AS";
    const diff = Math.abs(aWins - bWins);
    const leaderLabel =
      aWins > bWins
        ? teamA.map(getLastName).join("/")
        : teamB.map(getLastName).join("/");
    const holesLeft = totalHolesInRange - playedCount;

    if (diff === 0) return "AS";

    // Match is over — clinched before last hole
    if (diff > holesLeft && holesLeft > 0) {
      return `${leaderLabel} ${diff}&${holesLeft}`;
    }

    // Match finished all holes
    if (holesLeft === 0) {
      return `${leaderLabel} ${diff} UP`;
    }

    // Dormie — leader is up by exactly the holes remaining
    if (diff === holesLeft) {
      return `${leaderLabel} DORMIE`;
    }

    // Match still in progress
    return `${leaderLabel} ${diff} UP`;
  };

  // --- 11'S GAME LOGIC ---
  const getElevensStats = (p) => {
    const pId = typeof p === "object" ? p.id : p;
    const pKept = keptHoles[pId] || [];
    let totalNetRel = 0;

    const rawHcp = playerHandicaps[pId] || 0;
    const hcp = typeof rawHcp === "object" ? rawHcp.current : rawHcp;

    pKept.forEach((holeNum) => {
      const hole = selectedCourse?.holes[holeNum - 1];
      const rawScore = parseInt(scores[pId]?.[holeNum], 10);
      if (rawScore && hole) {
        totalNetRel +=
          rawScore -
          getStrokesForHole(hcp, hole.difficulty) - // 👈 USE hcp INSTEAD OF playerHandicaps[pName]
          hole.par;
      }
    });
    return {
      count: pKept.length,
      netRel:
        totalNetRel === 0
          ? "E"
          : totalNetRel > 0
          ? `+${totalNetRel}`
          : totalNetRel,
      rawNet: totalNetRel,
    };
  };

  // --- SKINS LOGIC ---
  const getSkinsData = () => {
    let carryOver = 0;
    const winners = {},
      wonHoles = {};
    players.forEach((p) => {
      const pId = typeof p === "object" ? p.id : p;
      winners[pId] = 0;
      wonHoles[pId] = [];
    });

    selectedCourse?.holes.forEach((h, i) => {
      const holeNum = i + 1;
      let minScore = 999,
        scoresOnHole = [];
      players.forEach((p) => {
        const pId = typeof p === "object" ? p.id : p;
        const raw = scores[pId]?.[holeNum];
        if (raw) {
          const net =
            parseInt(raw, 10) -
            getStrokesForHole(playerHandicaps[pId], h.difficulty);
          if (net < minScore) minScore = net;
          scoresOnHole.push({ player: pId, net });
        }
      });
      const holeWinners = scoresOnHole.filter((s) => s.net === minScore);
      if (holeWinners.length === 1) {
        winners[holeWinners[0].player] += 1 + carryOver;
        wonHoles[holeWinners[0].player].push(holeNum);
        carryOver = 0;
      } else if (scoresOnHole.length > 0) carryOver++;
    });
    return { winners, wonHoles };
  };

  const { winners: skinWinners, wonHoles } = getSkinsData();

  const sortedPlayers = isMajor
    ? [...players].sort((a, b) => {
        const idA = typeof a === "object" ? a.id : a;
        const idB = typeof b === "object" ? b.id : b;
        return getPlayerStats(idA).rawNetRel - getPlayerStats(idB).rawNetRel;
      })
    : players;
  const sortedElevens = [...players].sort((a, b) => {
    const idA = typeof a === "object" ? a.id : a;
    const idB = typeof b === "object" ? b.id : b;
    return getElevensStats(idA).rawNet - getElevensStats(idB).rawNet;
  });

  const sortedSkinsPlayers = [...players].sort((a, b) => {
    const idA = typeof a === "object" ? a.id : a;
    const idB = typeof b === "object" ? b.id : b;
    return skinWinners[idB] - skinWinners[idA];
  });

  return (
    <div
      className="screen leaderboard-view"
      style={{
        background: "#f8fafc",
        minHeight: "100vh",
        padding: "20px",
        paddingBottom: "100px",
      }}
    >
      {/* Back Button */}
      {!isPublicView && (
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: "#1a4731",
            fontWeight: "700",
            fontSize: "0.9rem",
            cursor: "pointer",
            marginBottom: "10px",
            padding: "5px 0",
          }}
        >
          {isHistorical ? "← Back" : "← Back to Scorecard"}
        </button>
      )}
      {/* Date Header */}
      {isHistorical && roundDate && (
        <div
          style={{
            textAlign: "center",
            fontSize: "0.85rem",
            color: "#64748b",
            fontWeight: "700",
            marginBottom: "15px",
            backgroundColor: "white",
            padding: "8px",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          📅{" "}
          {new Date(roundDate).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      )}

      {/* Title Banners */}
      {isMajor ? (
        <div
          style={{
            background: "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)",
            padding: "15px",
            borderRadius: "16px",
            textAlign: "center",
            marginBottom: "20px",
            border: "2px solid #fff",
          }}
        >
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: "900",
              color: "#451a03",
              letterSpacing: "2px",
            }}
          >
            OFFICIAL MAJOR CHAMPIONSHIP
          </div>
          <div style={{ fontSize: "1.3rem", fontWeight: "900", color: "#fff" }}>
            🏆 {majorName.toUpperCase()} 🏆
          </div>
        </div>
      ) : (
        <h2
          style={{
            color: "#1a4731",
            textAlign: "center",
            fontWeight: "900",
            fontSize: "1.5rem",
            marginBottom: isPublicView ? "8px" : "20px",
          }}
        >
          LEADERBOARD
        </h2>
      )}

      {isPublicView && (
        <div style={{ textAlign: "center", fontSize: "0.75rem", color: "#94a3b8", marginBottom: "16px" }}>
          Live Leaderboard • View Only
        </div>
      )}

      {/* --- NATIVE SMS SHARE BUTTON --- */}
      {isFinalized && !isPublicView && (
        <button
          onClick={handleShareSummary}
          style={{
            width: "100%",
            padding: "14px",
            backgroundColor: "#007aff",
            color: "white",
            border: "none",
            borderRadius: "14px",
            fontWeight: "900",
            fontSize: "0.85rem",
            marginBottom: "12px",
            boxShadow: "0 4px 12px rgba(0, 122, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          💬 TEXT ROUND SUMMARY
        </button>
      )}

      {/* --- SHARE LIVE LEADERBOARD BUTTON --- */}
      {!isPublicView && shareId && (
        <button
          onClick={() => {
            const url = `https://confident-ardinghelli-1.pages.dev/?view=live&share=${shareId}`;
            navigator.clipboard.writeText(url).then(() => {
              alert("Live leaderboard link copied! Anyone with this link can follow the round live, even without an account. The same link will show the final results after the round.");
            }).catch(() => {
              window.prompt("Copy this live leaderboard link:", url);
            });
          }}
          style={{
            width: "100%",
            padding: "14px",
            backgroundColor: "#2d6a4f",
            color: "white",
            border: "none",
            borderRadius: "14px",
            fontWeight: "900",
            fontSize: "0.85rem",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            cursor: "pointer",
          }}
        >
          🔗 Share Live Leaderboard
        </button>
      )}

      {/* --- SHARE LEADERBOARD LINK BUTTON --- */}
      {!isPublicView && roundId && (
        <button
          onClick={() => {
            const url = shareId
              ? `https://confident-ardinghelli-1.pages.dev/?view=live&share=${shareId}`
              : `https://confident-ardinghelli-1.pages.dev/?view=leaderboard&round=${roundId}`;

            const phoneNumbers = players
              .map((p) => {
                const pId = typeof p === "object" ? p.id : p;
                const profile = playerDirectory?.find((dir) => dir.id === pId);
                return profile?.phone_number || null;
              })
              .filter((num) => !!num)
              .map((num) => num.replace(/\D/g, ""))
              .map((num) => (num.length === 10 ? `+1${num}` : `+${num}`))
              .join(",");

            const message = `⛳ Follow our live golf leaderboard:\n${url}`;
            const encodedMsg = encodeURIComponent(message);
            window.location.href = `sms:${phoneNumbers}?&body=${encodedMsg}`;
          }}
          style={{
            width: "100%",
            padding: "14px",
            backgroundColor: "#1b4332",
            color: "white",
            border: "none",
            borderRadius: "14px",
            fontWeight: "900",
            fontSize: "0.85rem",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(27, 67, 50, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          🔗 Share Leaderboard
        </button>
      )}

      {/* Match Play Section */}
      {!isPublicView && selectedGames.includes("Match Play") && players.length >= 2 && (
        <div
          style={{
            background: "#1a4731",
            borderRadius: "16px",
            padding: "20px",
            marginBottom: "25px",
            color: "white",
            textAlign: "center",
          }}
        >
          <div
            style={{
              marginBottom: "16px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              paddingBottom: "10px",
            }}
          >
            <div style={{ fontSize: "0.7rem", opacity: 0.8 }}>MATCH TOTAL</div>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "900",
                color: "#fbbf24",
              }}
            >
              {getMatchStatus(1, 18)}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.65rem", opacity: 0.8 }}>FRONT 9</div>
              <div style={{ fontSize: "0.9rem", fontWeight: "800" }}>
                {getMatchStatus(1, 9)}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.65rem", opacity: 0.8 }}>BACK 9</div>
              <div style={{ fontSize: "0.9rem", fontWeight: "800" }}>
                {getMatchStatus(10, 18)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- VEGAS DISPLAY CARD --- */}
      {!isPublicView && selectedGames.includes("Vegas") &&
        vegasConfig &&
        vegasConfig.teamA.length === 2 && (
          <div
            style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
              borderRadius: "16px",
              padding: "20px",
              marginBottom: "25px",
              color: "white",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "900",
                  color: "#94a3b8",
                  letterSpacing: "1px",
                }}
              >
                VEGAS POINTS
              </span>
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "900",
                  padding: "4px 8px",
                  background: "rgba(255,255,255,0.1)",
                  borderRadius: "6px",
                }}
              >
                2v2
              </span>
            </div>

            {(() => {
              const { totalA, totalB, pressedA, pressedB, standardA, standardB } = calculateVegasScores(
                vegasConfig.teamA,
                vegasConfig.teamB,
                scores,
                selectedCourse?.holes || [],
                playerHandicaps,
                vegasPresses
              );
              const diff = totalA - totalB;

              const currentHole = scores && Object.keys(scores).length > 0
                ? Math.max(...Object.values(scores).flatMap(ps => Object.keys(ps).map(Number)))
                : 1;
              const currentNine = currentHole <= 9 ? "front" : "back";
              const teamACanPress = !isHistorical && totalA < totalB && vegasPresses?.[currentNine]?.teamA === null;
              const teamBCanPress = !isHistorical && totalB < totalA && vegasPresses?.[currentNine]?.teamB === null;

              const anyPressActive =
                vegasPresses?.front?.teamA !== null ||
                vegasPresses?.front?.teamB !== null ||
                vegasPresses?.back?.teamA !== null ||
                vegasPresses?.back?.teamB !== null;

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "900",
                          color: diff < 0 ? "#4ade80" : "white",
                        }}
                      >
                        {totalA}
                      </div>
                      <div style={{ fontSize: "0.6rem", color: "#94a3b8" }}>
                        {vegasConfig.teamA.map((p) => getLastName(p)).join("/")}
                      </div>
                    </div>

                    <div style={{ padding: "0 20px", textAlign: "center" }}>
                      {diff === 0 ? (
                        <div style={{ fontSize: "0.8rem", fontWeight: "900", color: "#fbbf24", background: "rgba(251,191,36,0.1)", padding: "4px 12px", borderRadius: "20px" }}>
                          EVEN
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                          <div style={{ fontSize: "0.75rem", fontWeight: "800", color: "#94a3b8", textAlign: "center" }}>
                            {diff > 0
                              ? vegasConfig.teamA.map(p => getLastName(p)).join("/")
                              : vegasConfig.teamB.map(p => getLastName(p)).join("/")} Owe
                          </div>
                          <div style={{ fontSize: "1.4rem", fontWeight: "900", color: "#fbbf24" }}>
                            {Math.abs(diff)}
                          </div>
                          {vegasPointValue > 0 && (
                            <div style={{ fontSize: "0.8rem", fontWeight: "800", color: "#4ade80", marginTop: "2px" }}>
                              ${(Math.abs(diff) * vegasPointValue).toFixed(2)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "center", flex: 1 }}>
                      <div
                        style={{
                          fontSize: "1.8rem",
                          fontWeight: "900",
                          color: diff > 0 ? "#4ade80" : "white",
                        }}
                      >
                        {totalB}
                      </div>
                      <div style={{ fontSize: "0.6rem", color: "#94a3b8" }}>
                        {vegasConfig.teamB.map((p) => getLastName(p)).join("/")}
                      </div>
                    </div>
                  </div>

                  {SHOW_VEGAS_PRESS && (teamACanPress || teamBCanPress) && (
                    <div style={{ marginTop: "12px", display: "flex", gap: "8px", justifyContent: "center" }}>
                      {teamACanPress && (
                        <button
                          onClick={() => onPress && onPress(currentNine, "teamA", currentHole)}
                          style={{
                            padding: "8px 16px",
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            fontWeight: "900",
                            fontSize: "0.8rem",
                            cursor: "pointer"
                          }}
                        >
                          🔴 {vegasConfig.teamA.map(p => getLastName(p)).join("/")} PRESS
                        </button>
                      )}
                      {teamBCanPress && (
                        <button
                          onClick={() => onPress && onPress(currentNine, "teamB", currentHole)}
                          style={{
                            padding: "8px 16px",
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "10px",
                            fontWeight: "900",
                            fontSize: "0.8rem",
                            cursor: "pointer"
                          }}
                        >
                          🔴 {vegasConfig.teamB.map(p => getLastName(p)).join("/")} PRESS
                        </button>
                      )}
                    </div>
                  )}

                  {SHOW_VEGAS_PRESS && anyPressActive && (
                    <div style={{
                      marginTop: "12px",
                      borderTop: "1px solid rgba(255,255,255,0.1)",
                      paddingTop: "12px",
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                      fontSize: "0.7rem",
                      color: "#94a3b8"
                    }}>
                      <div>
                        <div style={{ marginBottom: "4px", fontWeight: "800", textTransform: "uppercase", fontSize: "0.6rem" }}>Standard</div>
                        <div>{vegasConfig.teamA.map(p => getLastName(p)).join("/")} {standardA}</div>
                        <div>{vegasConfig.teamB.map(p => getLastName(p)).join("/")} {standardB}</div>
                      </div>
                      <div>
                        <div style={{ marginBottom: "4px", fontWeight: "800", textTransform: "uppercase", fontSize: "0.6rem", color: "#ef4444" }}>Pressed (2x)</div>
                        <div>{vegasConfig.teamA.map(p => getLastName(p)).join("/")} {pressedA}</div>
                        <div>{vegasConfig.teamB.map(p => getLastName(p)).join("/")} {pressedB}</div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

      {/* Main Stats Cards */}
      <div className="leaderboard-list">
        {sortedPlayers.map((p) => {
          const pId = typeof p === "object" ? p.id : p;
          const name = getName(p);
          const stats = getPlayerStats(pId);
          return (
            <div
              key={name}
              className="glass-card"
              style={{
                marginBottom: "15px",
                padding: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "white",
                borderRadius: "16px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
              onClick={() => {
                if (onSelectPlayer) onSelectPlayer(name);
                else if (setStatsTargetPlayer) {
                  setStatsTargetPlayer(name);
                  if (setView) setView("verticalScorecard");
                }
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: "800",
                    fontSize: "1.1rem",
                    color: "#1e293b",
                  }}
                >
                  {name}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                  HCP: {stats.hcp}{roundHandicapOverrides?.[pId] ? "*" : ""}
                </div>
              </div>

              <div
                style={{ display: "flex", gap: "12px", alignItems: "center" }}
              >
                {!isPublicView && selectedGames.includes("Quota Points") && (
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "0.55rem",
                        color: "#60a5fa",
                        fontWeight: "bold",
                      }}
                    >
                      QUOTA
                    </div>
                    <div
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "800",
                        color: "#2563eb",
                      }}
                    >
                      {stats.quotaRel}
                    </div>
                  </div>
                )}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "0.55rem",
                      color: "#94a3b8",
                      fontWeight: "bold",
                    }}
                  >
                    GROSS
                  </div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "800",
                      color: "#334155",
                    }}
                  >
                    {stats.grossRel}
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "0.55rem",
                      color: isMajor ? "#d97706" : "#16a34a",
                      fontWeight: "bold",
                    }}
                  >
                    {isMajor ? "TOURNEY" : "NET"}
                  </div>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "800",
                      color: isMajor ? "#fff" : "#16a34a",
                      background: isMajor ? "#fbbf24" : "#dcfce7",
                      padding: "2px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {stats.netRel}
                  </div>
                </div>

                {isFinalized && !isPublicView && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVenmoPayment(name);
                    }}
                    style={{
                      marginLeft: "10px",
                      backgroundColor: "#3d95ce",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      fontSize: "0.7rem",
                      fontWeight: "900",
                    }}
                  >
                    PAY
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {Object.keys(roundHandicapOverrides || {}).length > 0 && (
          <div style={{ fontSize: "0.65rem", color: "#94a3b8", textAlign: "center", marginTop: "8px", fontStyle: "italic" }}>
            * Handicap manually adjusted
          </div>
        )}
      </div>

      {/* 11's Standings */}
      {!isPublicView && selectedGames.includes("11's") && (
        <div style={{ marginTop: "30px" }}>
          <h3
            style={{
              color: "#1a4731",
              fontSize: "0.9rem",
              fontWeight: "900",
              marginBottom: "10px",
            }}
          >
            11'S GAME STANDINGS
          </h3>
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody style={{ fontSize: "0.9rem" }}>
                {sortedElevens.map((p) => {
                  const pId = typeof p === "object" ? p.id : p;
                  const name = getName(p);
                  const stats = getElevensStats(pId);
                  return (
                    <tr
                      key={name}
                      style={{ borderBottom: "1px solid #f1f5f9" }}
                    >
                      <td style={{ padding: "15px", fontWeight: "700" }}>
                        {getLastName(name)}
                      </td>
                      <td style={{ textAlign: "center", padding: "15px" }}>
                        <span
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "800",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            background:
                              stats.count === 11 ? "#dcfce7" : "#f1f5f9",
                            color: stats.count === 11 ? "#16a34a" : "#64748b",
                          }}
                        >
                          {stats.count === 11 ? "✓" : `${stats.count}/11`}
                        </span>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "15px",
                          fontWeight: "900",
                        }}
                      >
                        {stats.netRel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Skins Standings */}
      {!isPublicView && selectedGames.includes("Skins") && (
        <div style={{ marginTop: "30px" }}>
          <h3
            style={{
              color: "#1a4731",
              fontSize: "0.9rem",
              fontWeight: "900",
              marginBottom: "10px",
            }}
          >
            NET SKINS
          </h3>
          <div style={{ background: "white", borderRadius: "16px" }}>
            {sortedSkinsPlayers.map((p) => {
              const pId = typeof p === "object" ? p.id : p;
              const name = getName(p);
              const skinsWon = skinWinners[pId] || 0;

              if (skinsWon === 0) return null;

              return (
                <div
                  key={name}
                  style={{
                    padding: "15px",
                    borderBottom: "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: "700" }}>{getLastName(name)}</span>
                  <span
                    style={{
                      fontWeight: "900",
                      color: "#16a34a",
                      background: "#dcfce7",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                    }}
                  >
                    {skinsWon} Skins
                  </span>
                </div>
              );
            })}

            {/* Show message if no skins were won by anyone */}
            {Object.values(skinWinners).every((s) => s === 0) && (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontWeight: "600",
                  fontSize: "0.9rem",
                }}
              >
                No skins won today.
              </div>
            )}
          </div>
        </div>
      )}
      {/* --- NEW: ADVANCED STATS TOGGLE --- */}
      {!isHistorical && (
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "white",
            borderRadius: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <div>
            <h4
              style={{
                margin: "0 0 5px 0",
                color: "#1a4731",
                fontWeight: "800",
                fontSize: "1rem",
              }}
            >
              My Advanced Stats
            </h4>
            <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b" }}>
              Track Fairways, GIR & Putts
            </p>
          </div>
          <button
            onClick={() =>
              onTrackStats ? onTrackStats() : setView("holeStats")
            }
            style={{
              padding: "10px 16px",
              backgroundColor: "#1a4731",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontWeight: "900",
              fontSize: "0.8rem",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(26, 71, 49, 0.2)",
            }}
          >
            TRACK 📊
          </button>
        </div>
      )}
      {/* Finish & Save Button */}
      {!isHistorical && !isPublicView && (
        <button
          onClick={() => {
            if (window.confirm("Finish and save this round?")) {
              onFinish();
            }
          }}
          style={{
            width: "100%",
            padding: "14px",
            backgroundColor: "#1b4332",
            color: "white",
            border: "none",
            borderRadius: "14px",
            fontWeight: "900",
            fontSize: "0.85rem",
            marginTop: "12px",
            marginBottom: "12px",
            boxShadow: "0 4px 12px rgba(27, 67, 50, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            cursor: "pointer",
          }}
        >
          🏆 Finish & Save Round
        </button>
      )}
      {roundHistory && roundHistory.length > 1 && (
        <div style={{ marginTop: "30px" }}>
          <h3 style={{
            color: "#1a4731", fontSize: "0.9rem", fontWeight: "900",
            marginBottom: "10px", textTransform: "uppercase"
          }}>
            Past Rounds
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {[...roundHistory]
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .filter(r => r.id !== roundId)
              .map(round => {
                const playerCount = round.players?.length || 0;
                const netWinner = (() => {
                  if (!round.scores || !round.course_data?.holes) return null;
                  let bestNet = 999;
                  let winnerName = null;
                  round.players?.forEach(pid => {
                    const pScores = round.scores[pid] || {};
                    const gross = Object.values(pScores).reduce((sum, s) => sum + (Number(s) || 0), 0);
                    if (gross === 0) return;
                    const hcp = round.player_handicaps?.[pid] || 0;
                    const net = gross - hcp;
                    if (net < bestNet) {
                      bestNet = net;
                      const profile = playerDirectory.find(p => p.id === pid);
                      winnerName = profile?.full_name?.split(" ")[1] || profile?.full_name || pid;
                    }
                  });
                  return winnerName;
                })();

                return (
                  <div
                    key={round.id}
                    onClick={() => onSelectRound && onSelectRound(round)}
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "14px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      cursor: "pointer",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "700", color: "#1e293b", fontSize: "0.95rem" }}>
                        {new Date(round.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: "2px" }}>
                        {round.course_name} • {playerCount} players
                        {netWinner && ` • 🏆 ${netWinner}`}
                      </div>
                    </div>
                    <div style={{ color: "#94a3b8", fontSize: "1rem" }}>›</div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
